import { Dialog } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { applyToCompany } from "../../../redux/actions/company_action";
import { updateSignUpOrigin } from "../../../redux/actions/navbar_action";
import HomepageReplaceResumeDialog from "../../homepage/HomepageReplaceResumeDialog/HomepageReplaceResumeDialog";
import HomepageResumeUploadedDialog from "../../homepage/HomepageResumeUploadedDialog/HomepageResumeUploadedDialog";
import HomepageDropResume from "../../homepage/HomepageSearch/HomepageDropResume/HomepageDropResume";
import HomepageViewResumeDialog from "../../homepage/HomepageViewResumeDialog/HomepageViewResumeDialog";
import DropCvExistingResume from "./DropCvExistingResume";
import {
  ResumeSentToastContainer,
  SuccessToastContent,
  SuccessToastHeader,
  SuccessToastIcon,
  SuccessToastMessage,
  SuccessToastTitle,
} from "./styles";

const DropCvFlow = ({
  open,
  onClose,
  companyId,
  onSubmitted,
  signupSource = "company-profile",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [userResume, setUserResume] = useState(null);
  const [submittingInterest, setSubmittingInterest] = useState(false);
  const inputFileRef = useRef(null);
  const openedAsVisitorRef = useRef(false);
  const previousOpenRef = useRef(false);
  const reduxUser = useSelector((state) => state?.user?.user);
  const reduxUserId = reduxUser?.id;

  const handleClose = () => {
    onClose?.();
  };

  const handleFlowComplete = () => {
    onSubmitted?.();
    toast.success(resumeSentToast, {
      containerId: "company-resume-sent",
      icon: false,
      className: "company-resume-sent-toast",
    });
    handleClose();
  };

  const handleSubmitInterest = async () => {
    if (!companyId || submittingInterest) return;

    setSubmittingInterest(true);

    try {
      const result = await dispatch(applyToCompany({ companyId }));

      if (result?.success) {
        handleFlowComplete();
        return;
      }

      toast.error(
        result?.errors?.[0]?.message ||
          t("homepage.drop_resume_upload_failure_title")
      );
    } catch {
      toast.error(t("homepage.drop_resume_upload_failure_title"));
    } finally {
      setSubmittingInterest(false);
    }
  };

  const resumeSentToast = (
    <SuccessToastContent>
      <SuccessToastHeader>
        <SuccessToastIcon />
        <SuccessToastTitle>
          {t("companydetail.work_here_success_title")}
        </SuccessToastTitle>
      </SuccessToastHeader>

      <SuccessToastMessage>
        {t("companydetail.work_here_success_message")}
      </SuccessToastMessage>
    </SuccessToastContent>
  );

  const triggerToast = (isSuccess, content) => {
    if (isSuccess) {
      toast.success(t("homepage.drop_resume_success_message"));
      return;
    }

    if (content) {
      toast.error(content);
      return;
    }

    toast.error(t("homepage.drop_resume_upload_failure_title"));
  };

  useEffect(() => {
    if (open && !previousOpenRef.current) {
      openedAsVisitorRef.current = !reduxUserId;
      dispatch(updateSignUpOrigin(signupSource));
    }

    previousOpenRef.current = open;
  }, [dispatch, open, reduxUserId, signupSource]);

  useEffect(() => {
    if (open && openedAsVisitorRef.current && reduxUserId) {
      handleClose();
    }
  }, [open, reduxUserId]);

  return (
    <>
      <HomepageResumeUploadedDialog
        inputFileRef={inputFileRef}
        triggerToast={triggerToast}
        userResume={userResume}
        setUserResume={setUserResume}
        onFlowComplete={handleSubmitInterest}
        titleKey="companydetail.work_here_signup_title"
        createAccountKey="companydetail.work_here_signup_create_account"
        detailsTitleKey="companydetail.work_here_signup_details"
        submitKey="companydetail.work_here_submit"
      />

      <HomepageReplaceResumeDialog
        triggerToast={triggerToast}
        userResume={userResume}
        setUserResume={setUserResume}
        onCloseModal={handleClose}
        onFlowComplete={handleSubmitInterest}
      />

      <HomepageViewResumeDialog
        userResume={userResume}
        existingResumeUrl={reduxUser?.resume}
      />

      <ResumeSentToastContainer
        containerId="company-resume-sent"
        limit={1}
        position="top-right"
      />

      <Dialog
        open={open}
        onClose={handleClose}
        data-testid="company-drop-cv-dialog"
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: "calc(100% - 32px)",
            maxWidth: "560px",
            m: 2,
            borderRadius: "16px",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <HomepageDropResume
          inputFileRef={inputFileRef}
          triggerToast={triggerToast}
          userResume={userResume}
          setUserResume={setUserResume}
          isFloatingModal
          onCloseModal={handleClose}
          onFlowComplete={handleSubmitInterest}
          titleKey="companydetail.work_here_resume_title"
          descriptionKey="companydetail.work_here_resume_description"
          renderContent={
            reduxUser?.resume
              ? ({ handleFileChange, handleUploadButton }) => (
                  <DropCvExistingResume
                    user={reduxUser}
                    inputFileRef={inputFileRef}
                    onFileChange={handleFileChange}
                    onReupload={handleUploadButton}
                    onClose={handleClose}
                    onSubmit={handleSubmitInterest}
                    isSubmitting={submittingInterest}
                  />
                )
              : undefined
          }
        />
      </Dialog>
    </>
  );
};

export default DropCvFlow;
